import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  DrawerBody,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';
import { Baby, CircleX, GitPullRequestArrow, Redo2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { EnrolmentHeader } from './EnrolmentHeader';
import { EnrolmentIssues } from './EnrolmentIssues';
import { EnrolmentIssuesItem } from './types';
import VerifyUsiForm from './VerifyUsiForm';
import RestoreEnrolmentModal from './RestoreEnrolmentModal';
import { ResolveGuardianConsentModal } from './ResolveGuardianConsentModal';
import { ResolveRecognitionForm } from './ResolveRecognitionForm';
import type { USIResponse } from '@duke-hq/usi/types';
import { NoteList } from '../Notes/NoteList';
import { SemesterCompletionAlert } from '../components/SemesterCompletionAlert';
import { StudentCourseEnrolmentStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/StudentCourseEnrolmentStatus.schema';
import { StudentCourseEnrolmentStatus } from '@duke-hq/svc-api-db';
import { isUSIVerified } from '@duke-hq/lib/usi';
import CourseItemsStatusAccordion from '@duke-hq/enrolments-ui/components/CourseItemsStatusAccordion/CourseItemsStatusAccordion';
import { getStructuredUnitOfCompetencies } from '@duke-hq/svc-api/lib/enrolment/getStructuredUnitOfCompetencies';
import type { UnitActionsProps } from '@duke-hq/enrolments-ui/components/CourseItemsStatusAccordion/types';
import { UnitAdjustments } from '../components/UnitAdjustments';
import { useCheckPermission } from '@duke-hq/app-admin-web/hooks/useCheckPermission';
import { i18n } from '@lingui/core';
import { skipToken } from '@tanstack/react-query';
import { StudentContactsList } from '../SupportTasks/StudentContactsList';
import DialogBox from '@duke-hq/ui/components/DialogBox/DialogBox';

export function EnrolmentDetails(): React.JSX.Element {
  const { enrolmentId } = useParams<{ enrolmentId: string }>();
  const [verifyUSIResponse, setVerifyUSIResponse] = useState<USIResponse | null | undefined>(
    undefined,
  );

  const {
    isOpen: isOpenVerifyUSI,
    onOpen: onOpenVerifyUSI,
    onClose: onCloseVerifyUSI,
  } = useDisclosure();

  const {
    isOpen: isOpenResolveRecognition,
    onOpen: onOpenResolveRecognition,
    onClose: onCloseResolveRecognition,
  } = useDisclosure();

  const [recognitionType, setRecognitionType] = useState<'ct' | 'rpl'>('ct');

  const {
    isOpen: isOpenResolveGuardianConsent,
    onOpen: onOpenResolveGuardianConsent,
    onClose: onCloseResolveGuardianConsent,
  } = useDisclosure();

  const { isOpen: isOpenRestore, onOpen: onOpenRestore, onClose: onCloseRestore } = useDisclosure();

  const tabs = useMemo(
    () => [
      { value: 'progress', label: t`Unit progress` },
      { value: 'issues', label: t`Issues` },
      { value: 'notes', label: t`Admin Notes` },
      { value: 'contacts', label: t`Contacts` },
    ],
    [],
  );

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<number>(() =>
    Math.max(
      tabs.findIndex((tab) => tab.value === searchParams.get('tab')),
      0,
    ),
  );

  const handleTabChange = useCallback(
    (index: number) => {
      if (typeof tabs[index] !== 'undefined') {
        setActiveTab(index);
      } else {
        setActiveTab(0);
      }
    },
    [tabs],
  );

  const {
    data: enrolment,
    isLoading,
    refetch,
  } = trpcAdmin.enrolment.byIdWithResourceStatus.useQuery(
    {
      enrolmentId: Number(enrolmentId),
    },
    { enabled: Number.isInteger(Number(enrolmentId)) },
  );

  const userDrn = enrolment?.studentDrn ?? null;

  // A paused enrolment can have several schedule adjustments (one per manually
  // re-dated unit, plus the one the pause creates). The resume date is the
  // latest UNIT adjustment — matching the auto-unpause task's selection.
  const resumeAdjustment = useMemo(() => {
    return enrolment?.CourseEnrolmentScheduleAdjustments?.filter(
      (adjustment) => adjustment.item_type === 'UNIT',
    ).sort(
      (a, b) => djs(b.scheduled_start_date).valueOf() - djs(a.scheduled_start_date).valueOf(),
    )[0];
  }, [enrolment?.CourseEnrolmentScheduleAdjustments]);

  const { data: assessmentsInRevision } = trpcAdmin.enrolment.assessmentsInRevision.useQuery(
    {
      courseEnrolmentId: Number(enrolmentId),
    },
    { enabled: Number.isInteger(Number(enrolmentId)) },
  );

  const { data: pinnedNotesCount } = trpcAdmin.students.pinnedNotesCount.useQuery(
    userDrn != null
      ? {
          drn: userDrn,
        }
      : skipToken,
    { enabled: Boolean(userDrn) },
  );

  const utils = trpcAdmin.useUtils();

  const verifyUSI = trpcAdmin.enrolment.verifyUSI.useMutation({
    onSuccess: (results) => {
      if (results.verified) {
        toast({
          status: 'success',
          title: t`USI has been verified successfully.`,
        });
        onCloseVerifyUSI();
      } else {
        setVerifyUSIResponse(results.usiResponse);
      }
    },
  });

  const resolveRecognition = trpcAdmin.enrolment.resolveRecognition.useMutation({
    onSuccess: async () => {
      toast({
        status: 'success',
        title: t`Recognition request has been resolved`,
      });
      onCloseResolveRecognition();
      await utils.invalidate();
    },
  });

  const { mutate: studentScheduleMutate, isPending: studentScheduleIsLoading } =
    trpcAdmin.enrolment.teardownAndRecreateStudentSchedule.useMutation({
      onSuccess: async () => {
        toast({
          status: 'success',
          title: t`Student schedule has been recreated successfully.`,
        });

        await refetch();
      },
      onError(error) {
        toast({
          status: 'error',
          title: t`An error occurred while recreating the student schedule: ${error?.message}`,
        });
      },
    });

  const toast = useToast();

  const handleIssue = useCallback(
    (issue: string) => {
      if (issue === 'usi' && enrolment?.tenant?.id != null) {
        onOpenVerifyUSI();
      } else if (issue === 'ct' || issue === 'rpl') {
        setRecognitionType(issue);
        onOpenResolveRecognition();
      } else if (issue === 'guardianConsent') {
        onOpenResolveGuardianConsent();
      }
    },
    [enrolment, onOpenVerifyUSI, onOpenResolveRecognition, onOpenResolveGuardianConsent],
  );

  const onVerifyUSI = async (data: { use_single_name: boolean; bypass_verification: boolean }) => {
    if (enrolment) {
      try {
        await verifyUSI.mutateAsync({
          id: enrolment.user.id,
          useSingleName: data.use_single_name,
          useUSIService: !data.bypass_verification,
        });
        await utils.invalidate();
      } catch (error) {
        toast({
          status: 'error',
          title: error instanceof Error ? error.message : t`An unknown error occurred`,
        });
      }
    }
  };

  const onResolveRecognition = async (data: { adminNote: string }) => {
    if (enrolment) {
      try {
        await resolveRecognition.mutateAsync({
          enrolmentId: enrolment.id,
          type: recognitionType,
          adminNote: data.adminNote,
        });
      } catch (error) {
        toast({
          status: 'error',
          title: error instanceof Error ? error.message : t`An unknown error occurred`,
        });
      }
    }
  };

  const recreateStudentSchedule = useCallback(() => {
    studentScheduleMutate({ enrolmentId: Number(enrolmentId) });
  }, [enrolmentId, studentScheduleMutate]);

  const canAssessWorkPlacements = useCheckPermission('canAssessWorkPlacements');
  const canViewAssessments = useCheckPermission('canViewAssessments');
  const canManageStudentDocuments = useCheckPermission('canManageStudentDocuments');

  const {
    isOpen: isMarkAllWpOpen,
    onOpen: onMarkAllWpOpen,
    onClose: onMarkAllWpClose,
  } = useDisclosure();

  const [allWpNotes, setAllWpNotes] = useState('');

  const incompleteWpUocIds = useMemo(() => {
    if (!enrolment) {
      return [];
    }
    const completedSet = new Set(enrolment.completedWorkPlacementUocIds);
    return Object.values(enrolment.unitsOfCompetencyById)
      .filter((unit) => unit.work_placement && !completedSet.has(unit.id))
      .map((unit) => unit.id);
  }, [enrolment]);

  const { data: wouldCompleteCourseData } =
    trpcAdmin.enrolment.getWouldCompleteCourseOnWorkPlacement.useQuery(
      {
        courseEnrolmentId: Number(enrolmentId),
        unitOfCompetencyIds: incompleteWpUocIds,
      },
      { enabled: isMarkAllWpOpen && incompleteWpUocIds.length > 0 },
    );

  const wouldCompleteCourse = wouldCompleteCourseData?.wouldCompleteCourse ?? false;

  const markAllWpComplete = trpcAdmin.workPlacements.placements.markComplete.useMutation({
    onSuccess() {
      toast({
        status: 'success',
        title: t`All work placements marked as complete`,
      });
      onMarkAllWpClose();
      setAllWpNotes('');
      void utils.enrolment.byIdWithResourceStatus.invalidate({
        enrolmentId: Number(enrolmentId),
      });
    },
    onError(error) {
      toast({
        status: 'error',
        title: error.message,
      });
    },
  });

  const UnitAdjustmentsWithWP = useMemo(
    () =>
      function UnitAdjustmentsWrapper(props: UnitActionsProps) {
        const unit = enrolment?.unitsOfCompetencyById[props.unitOfCompetencyId];
        const isWorkPlacementComplete =
          enrolment?.completedWorkPlacementUocIds.includes(props.unitOfCompetencyId) ?? false;
        return (
          <UnitAdjustments
            {...props}
            isWorkPlacement={unit?.work_placement ?? false}
            isWorkPlacementComplete={isWorkPlacementComplete}
          />
        );
      },
    [enrolment],
  );

  const usiVerified = useCallback(
    (
      studentData:
        | { usi_verified_at: Date | null; usi_verified_by: string | null }
        | null
        | undefined,
    ): boolean => {
      const usiVerifiedAt = studentData?.usi_verified_at;
      const usiVerifiedBy = studentData?.usi_verified_by;

      // Check if USI is NOT verified (returns true when there's an issue)
      return !(usiVerifiedAt != null && usiVerifiedBy != null);
    },
    [],
  );

  const checkEnrolmentDetails = useCallback(
    (
      studentData:
        | {
            usi_verified_at: Date | null;
            usi_verified_by: string | null;
          }
        | null
        | undefined,
      enrolmentData?: {
        applyForCreditTransfer: boolean | null;
        applyForRpl: boolean | null;
        ctResolvedAt: Date | null;
        rplResolvedAt: Date | null;
        guardianConsentRequiredAt: Date | null;
        guardianConsentResolvedAt: Date | null;
      } | null,
    ): EnrolmentIssuesItem => {
      const usiIssue = usiVerified(studentData);
      const ctIsRequested = enrolmentData?.applyForCreditTransfer === true;
      const rplIsRequested = enrolmentData?.applyForRpl === true;
      const ctIsResolved = enrolmentData?.ctResolvedAt != null;
      const rplIsResolved = enrolmentData?.rplResolvedAt != null;
      const guardianConsentIsRequired = enrolmentData?.guardianConsentRequiredAt != null;
      const guardianConsentIsResolved = enrolmentData?.guardianConsentResolvedAt != null;

      const enrolmentIssues = {
        usi: {
          issue: usiIssue,
          label: 'USI Required',
          icon: CircleX,
          color: 'danger',
          status: usiIssue ? 'Open' : 'Resolved',
        },
        ct: {
          issue: ctIsRequested,
          label: 'CT Required',
          icon: GitPullRequestArrow,
          color: 'warning',
          status: ctIsResolved ? 'Resolved' : ctIsRequested ? 'Open' : undefined,
        },
        rpl: {
          issue: rplIsRequested,
          label: 'RPL Required',
          icon: GitPullRequestArrow,
          color: 'warning',
          status: rplIsResolved ? 'Resolved' : rplIsRequested ? 'Open' : undefined,
        },
        guardianConsent: {
          issue: guardianConsentIsRequired,
          label: 'Guardian consent required',
          icon: Baby,
          color: 'warning',
          status: guardianConsentIsResolved
            ? 'Resolved'
            : guardianConsentIsRequired
              ? 'Open'
              : undefined,
          canResolve: canManageStudentDocuments,
        },
      };

      // Filter to only include issues where the issue is true, excluding 'usi'
      return Object.fromEntries(
        Object.entries(enrolmentIssues).filter(([key, value]) => key === 'usi' || value.issue),
      );
    },
    [usiVerified, canManageStudentDocuments],
  );

  const issues = useMemo(() => {
    if (enrolment?.user?.studentData) {
      return checkEnrolmentDetails(enrolment.user.studentData, enrolment);
    }
    return {} as EnrolmentIssuesItem;
  }, [checkEnrolmentDetails, enrolment]);

  const issuesCount = useMemo(
    () =>
      Object.values(issues).filter((issue) => {
        return (
          typeof issue === 'object' &&
          issue !== null &&
          'issue' in issue &&
          typeof issue.issue === 'boolean' &&
          issue.issue &&
          issue.status !== 'Resolved'
        );
      }).length,
    [issues],
  );

  const processedUnits = useMemo(
    () =>
      enrolment
        ? getStructuredUnitOfCompetencies(
            enrolment.courseEnrolmentScheduleItems,
            enrolment.unitsOfCompetencyById,
            new Set<string>(assessmentsInRevision?.map((item) => item.unitOfCompetency.id)),
            enrolment,
            enrolment.completedWorkPlacementUocIds,
          )
        : {},
    [enrolment, assessmentsInRevision],
  );

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!enrolment) {
    return (
      <Alert>
        <Trans>Enrolment not found</Trans>
      </Alert>
    );
  }

  return (
    <Flex flexDirection="column" w="100%" h="100%">
      <EnrolmentHeader
        assessmentsInRevision={assessmentsInRevision}
        enrolment={enrolment}
        onMarkAllWpComplete={
          canAssessWorkPlacements && incompleteWpUocIds.length > 0 ? onMarkAllWpOpen : undefined
        }
      />
      {enrolment.status === StudentCourseEnrolmentStatus.WITHDRAWN ? (
        <Box p={0}>
          <Alert bg="gray.100" justifyContent="space-between" alignItems="center">
            <Flex alignItems="center">
              <AlertIcon color="gray.700" />
              <Text color="gray.700" fontSize="sm">
                <Trans>This enrolment was withdrawn on the</Trans>{' '}
                {djs(enrolment.withdrawn_at).format('D MMM YYYY')}
              </Text>
            </Flex>
            <Button
              variant="outline"
              colorScheme="gray"
              bg="bg.canvas"
              px={2}
              h="26px"
              fontWeight="medium"
              fontSize="xs"
              borderRadius={8}
              leftIcon={<Redo2 size="14px" />}
              color="gray.700"
              onClick={onOpenRestore}
            >
              <Trans>Restore</Trans>
            </Button>
          </Alert>
          <RestoreEnrolmentModal
            enrolmentId={enrolment.id}
            studentId={enrolment.user_id}
            isOpen={isOpenRestore}
            onClose={onCloseRestore}
          />
        </Box>
      ) : null}

      {enrolment.status === StudentCourseEnrolmentStatus.DELETED &&
      enrolment.deleted_at !== null ? (
        <Box p={0}>
          <Alert status="warning" justifyContent="space-between" alignItems="center">
            <Flex alignItems="center">
              <AlertIcon color="gray.700" />
              <Text color="gray.700" fontSize="sm">
                <Trans>
                  This enrolment was deleted on{' '}
                  {i18n.date(enrolment.deleted_at, { dateStyle: 'medium' })}
                </Trans>
              </Text>
            </Flex>
          </Alert>
        </Box>
      ) : null}

      {enrolment.status === StudentCourseEnrolmentStatusSchema.enum.PAUSED &&
      resumeAdjustment != null ? (
        <Box p={0}>
          <Alert
            status="info"
            colorScheme="cyan"
            justifyContent="space-between"
            alignItems="center"
          >
            <AlertDescription>
              <Flex alignItems="center">
                <HStack>
                  <AlertIcon />
                </HStack>
                <Text color="gray.700" fontSize="sm">
                  <Trans>This enrolment is paused and will resume on</Trans>{' '}
                  {djs(resumeAdjustment.scheduled_start_date).format('D MMM YYYY')}
                </Text>
              </Flex>
            </AlertDescription>
          </Alert>
        </Box>
      ) : null}

      <DrawerBody p="0" border="none" m="0" paddingInline="0 !important">
        <Tabs
          isLazy
          flexDirection="column"
          display="flex"
          w="100%"
          h="100%"
          index={activeTab}
          onChange={handleTabChange}
        >
          <TabList bg="bg.subtle">
            {tabs.map((tab, index) => (
              <Tab
                key={tab.value}
                bg={activeTab === index ? 'bg.surface' : ''}
                _hover={{ bg: 'bg.muted' }}
              >
                {tab.label}
                {tab.value === 'issues' && issuesCount > 0 ? (
                  <Badge
                    borderRadius="full"
                    borderColor="danger"
                    borderWidth="1px"
                    backgroundColor="danger"
                    padding={issuesCount === 1 ? '2px 9px' : '2px 8px'}
                    ml="3"
                  >
                    <Text fontSize="xs" color="white">
                      {issuesCount}
                    </Text>
                  </Badge>
                ) : null}
                {tab.value === 'notes' && pinnedNotesCount != null && pinnedNotesCount > 0 ? (
                  <Badge
                    borderRadius="full"
                    borderColor="blue.500"
                    borderWidth="1px"
                    backgroundColor="blue.500"
                    padding={pinnedNotesCount === 1 ? '2px 9px' : '2px 8px'}
                    ml="3"
                  >
                    <Text fontSize="xs" color="white">
                      {pinnedNotesCount}
                    </Text>
                  </Badge>
                ) : null}
              </Tab>
            ))}

            <Box flex="1" display="flex" alignItems="center" justifyContent="flex-end" px={4}>
              <Button
                isLoading={studentScheduleIsLoading}
                variant="outline"
                size="xs"
                onClick={recreateStudentSchedule}
              >
                <Trans>Recreate student schedule</Trans>
              </Button>
            </Box>
          </TabList>

          {enrolment != null ? (
            <TabPanels width="100%" height="100%" overflow="auto">
              <TabPanel width="100%" height="100%" overflow="scroll" p={0}>
                <CourseItemsStatusAccordion
                  courseEnrolment={enrolment}
                  processedUnits={processedUnits}
                  UnitActionsComponent={UnitAdjustmentsWithWP}
                  getAssessmentLink={
                    canViewAssessments
                      ? (submissionId) => `/assessments/${submissionId}`
                      : undefined
                  }
                  labels={{
                    semester: (n: number) => t`Semester ${n}`,
                    lln: t`Language, Literacy, and Numeracy`,
                    skillSetUnits: t`Skill set units`,
                    enrolled: t`Enrolled`,
                    notEnrolled: t`Not Enrolled`,
                    unit: t`Unit`,
                    releaseDate: t`Release date`,
                    status: t`Status`,
                  }}
                />
              </TabPanel>

              <TabPanel width="100%" height="100%" overflow="scroll" p={0}>
                <EnrolmentIssues issues={issues} onIssue={handleIssue} />
              </TabPanel>

              <TabPanel width="100%" height="100%" overflow="scroll" p={0}>
                {userDrn != null && enrolment.tenant_id != null ? (
                  <NoteList
                    userDrn={userDrn}
                    tenantId={enrolment.tenant_id}
                    consultantId={enrolment.consultant_id ?? undefined}
                  />
                ) : null}
              </TabPanel>

              <TabPanel width="100%" height="100%" overflow="scroll" p={0}>
                {enrolment != null && userDrn != null && enrolmentId != null ? (
                  <Box p={4}>
                    <StudentContactsList studentDrn={userDrn} enrolmentId={Number(enrolmentId)} />
                  </Box>
                ) : null}
              </TabPanel>
            </TabPanels>
          ) : null}
        </Tabs>
      </DrawerBody>

      <Modal isOpen={isOpenVerifyUSI} onClose={onCloseVerifyUSI}>
        <ModalOverlay />
        <ModalContent padding={4}>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VerifyUsiForm
              universal_student_identifier={
                enrolment.user.studentData?.universal_student_identifier ?? ''
              }
              first_name={enrolment.user.first_name ?? ''}
              last_name={enrolment.user.last_name ?? ''}
              date_of_birth={enrolment.user.studentData?.date_of_birth}
              onVerifyUSI={onVerifyUSI}
              verifyUSIResponse={verifyUSIResponse}
              isUSIVerified={isUSIVerified(verifyUSIResponse)}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenResolveRecognition} onClose={onCloseResolveRecognition}>
        <ModalOverlay />
        <ModalContent padding={4}>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <ResolveRecognitionForm type={recognitionType} onSubmit={onResolveRecognition} />
          </ModalBody>
        </ModalContent>
      </Modal>

      {isOpenResolveGuardianConsent ? (
        <ResolveGuardianConsentModal
          enrolmentId={enrolment.id}
          studentId={enrolment.user.id}
          onClose={onCloseResolveGuardianConsent}
        />
      ) : null}

      {/* Mark all work placements complete */}
      <DialogBox
        size="xl"
        isOpen={isMarkAllWpOpen}
        onClose={onMarkAllWpClose}
        showCloseButton
        header={<Trans>Mark all work placements complete</Trans>}
        body={
          <VStack>
            {wouldCompleteCourse ? <SemesterCompletionAlert /> : null}
            <FormControl>
              <FormLabel color="fg.emphasis" fontWeight="normal" fontSize="sm" mb={1}>
                <Trans>Notes (optional)</Trans>
              </FormLabel>
              <Textarea
                height="auto"
                rows={5}
                mb={2}
                value={allWpNotes}
                onChange={(e) => setAllWpNotes(e.target.value)}
              />
            </FormControl>
          </VStack>
        }
        footer={
          <>
            <Button variant="ghost" mr={3} onClick={onMarkAllWpClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              isLoading={markAllWpComplete.isPending}
              isDisabled={markAllWpComplete.isPending}
              onClick={() =>
                void markAllWpComplete.mutateAsync({
                  courseEnrolmentId: Number(enrolmentId),
                  unitOfCompetencyIds: incompleteWpUocIds,
                  notes: allWpNotes || undefined,
                })
              }
            >
              <Trans>Mark as complete</Trans>
            </Button>
          </>
        }
      />
    </Flex>
  );
}
